import { useCallback, useMemo, useState } from 'react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { useAuth } from '../../../global/contexts/auth/auth';
import { useTokens } from '../../../global/contexts/lookups/lookups';
import { useLiveNotices } from './updates';
import { liveKeys } from '../queries/keys';
import { ALL_MODALITIES } from '../../tasks/queries/paths';
import { livePaths } from '../queries/paths';
import { COUNTED_STATES, countDeltas, edgeByKind } from '../utils/flow';
import {
    FEED_SIZE, mergeRow, needsAuthor, normalizeEvent, normalizeNotice, withAuthor,
} from '../utils/notice';

// TSS vite/03 — server state for the pulse.
//
// THE STATE IS ASKED FOR ONCE AND THEN LISTENED TO. `staleTime: Infinity` plus
// both refetch switches off is not a cache tweak: it is the first acceptance
// criterion of this task, written as configuration. The project's defaults
// refetch on focus and on reconnect, and either one would quietly turn the
// screen into a poller that happens to also have a channel — at which point
// nobody could tell whether the channel works.
//
// The notices do not live beside the cache, they live IN it: `setQueryData`
// prepends each arrival to the same rows the HTTP load left, and moves the
// per-state counts the graph draws. A parallel `useState` list would be a
// second copy of server state, which vite/03 forbids for the reason this
// screen would have hit within a day — the two copies disagreeing after a
// resync.

export function useLivePulse({ projectId } = {}) {
    const client = useQueryClient();
    const [applied, setApplied] = useState(0);

    const feedParams = useMemo(() => {
        const query = { limit: FEED_SIZE, offset: 0 };
        if (projectId) query.project_id = projectId;
        return query;
    }, [projectId]);

    const feedKey = useMemo(() => liveKeys.feed(feedParams), [feedParams]);

    const feed = useQuery({
        queryKey: feedKey,
        queryFn: ({ signal }) => api
            .get(livePaths.events(), { params: feedParams, signal })
            .then(response => ({
                rows: (response.data?.items ?? []).map(normalizeEvent),
                total: response.data?.pagination?.total ?? 0,
            })),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    // One query per state, and each one asks for a single row: what is wanted
    // is `pagination.total`, which the list endpoint carries on every answer.
    // `workflow_state` filters by equality, so seven states cannot be one
    // request — the same reason the board asks eight times.
    const countKey = useCallback((state) => liveKeys.count(
        projectId ? { workflow_state: state, project_id: projectId } : { workflow_state: state },
    ), [projectId]);

    const countQueries = useQueries({
        queries: COUNTED_STATES.map(state => ({
            queryKey: countKey(state),
            queryFn: ({ signal }) => {
                // Both modalities: the graph counts all work, planned or one-off.
                const params = { limit: 1, offset: 0, workflow_state: state, modality: ALL_MODALITIES };
                if (projectId) params.project_id = projectId;
                return api
                    .get(livePaths.tasks(), { params, signal })
                    .then(response => response.data?.pagination?.total ?? 0);
            },
            staleTime: Infinity,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            // Moved by hand from the notices (countDeltas below). The panel-wide
            // refresh in contexts/updates leaves these alone: refetching them on
            // every notice would undo exactly what they do.
            meta: { liveManaged: true },
        })),
    });

    const counts = useMemo(() => Object.fromEntries(
        COUNTED_STATES.map((state, index) => [state, countQueries[index]?.data ?? null]),
    ), [countQueries]);

    const handleNotice = useCallback((payload) => {
        const incoming = normalizeNotice(payload);
        if (!incoming) return;

        // The server already filters by project (T15.4), so this only catches
        // the window between changing the filter and the new subscription
        // taking effect. Dropping it here is cheaper than a row that appears
        // under a filter that excludes it.
        if (projectId && incoming.projectId != null
            && String(incoming.projectId) !== String(projectId)) return;

        // THE AUTHOR IS ASKED FOR, because the notice does not bring it. Only
        // when nobody on screen knows it already: a replay of a row the HTTP
        // load left has its author, and asking again would be a request per
        // replayed notice for nothing.
        const known = client.getQueryData(feedKey)?.rows?.find(item => item.id === incoming.id);
        const lookup = needsAuthor(incoming) && (!known || needsAuthor(known));
        if (lookup) incoming.authorPending = true;

        client.setQueryData(feedKey, (current) => {
            const rows = current?.rows ?? [];
            const isKnown = rows.some(item => item.id === incoming.id);
            return {
                rows: mergeRow(rows, incoming),
                // A replayed notice is not a new event. Counting it would make
                // the total climb every time a reconnection replays a gap.
                total: (current?.total ?? 0) + (isKnown ? 0 : 1),
            };
        });

        // The counts move with the transition the notice names — the kind IS
        // the edge, so the source state is known without asking. Never below
        // zero: a delta that did not apply cleanly should leave a number that
        // looks stale, not one that looks impossible.
        const deltas = countDeltas(incoming.kind);
        for (const [state, delta] of Object.entries(deltas)) {
            client.setQueryData(countKey(state), (current) => (
                typeof current === 'number' ? Math.max(0, current + delta) : current
            ));
        }
        if (Object.keys(deltas).length) setApplied(value => value + 1);

        // Who holds what changed. Not patched by hand: the notice says the task
        // moved, not which token took it, and the roster needs the token.
        const edge = edgeByKind(incoming.kind);
        if (edge && edge.from !== edge.to && (edge.from === 'claimed' || edge.to === 'claimed')) {
            client.invalidateQueries({ queryKey: liveKeys.holds });
        }

        if (lookup) {
            client
                .fetchQuery({
                    queryKey: liveKeys.event(incoming.id),
                    queryFn: ({ signal }) => api
                        .get(livePaths.event(incoming.id), { signal })
                        .then(response => response.data),
                    staleTime: Infinity,
                })
                // A failed lookup is not an error on screen: the movement
                // happened and is listed. The row only stops saying "asking".
                .catch(() => null)
                .then(event => {
                    client.setQueryData(feedKey, (current) => (current
                        ? { ...current, rows: withAuthor(current.rows, incoming.id, event) }
                        : current));
                });
        }
    }, [client, feedKey, countKey, projectId]);

    // The channel says the resume point is too far back to replay. The state
    // gets asked for again, and nobody had to press anything — which is the
    // second acceptance criterion.
    const handleResync = useCallback(() => {
        client.invalidateQueries({ queryKey: feedKey });
        client.invalidateQueries({ queryKey: liveKeys.counts });
        setApplied(0);
    }, [client, feedKey]);

    // THE PANEL'S CHANNEL, not one of its own. It used to open a connection
    // here; now there is one for the whole session (contexts/updates), and this
    // screen subscribes to it. The server no longer filters by project for it —
    // `handleNotice` already drops what belongs to another one.
    const live = useLiveNotices({ notice: handleNotice, resync: handleResync });
    const stream = live.stream;

    const { refetch } = feed;
    const reopen = live.reconnect;

    const recount = useCallback(async () => {
        await client.invalidateQueries({ queryKey: liveKeys.counts });
        setApplied(0);
    }, [client]);

    // Reload FIRST, then reopen. A hand-made connection carries no
    // `Last-Event-ID` header, so nothing replays the gap: the only honest way
    // back is to ask for the state again.
    const reconnect = useCallback(async () => {
        await refetch();
        await recount();
        reopen();
    }, [refetch, recount, reopen]);

    return {
        rows: feed.data?.rows ?? [],
        total: feed.data?.total ?? 0,
        counts,
        // How many notices moved a number since the last count. It is what
        // separates "this is what the backend said" from "this is what the
        // backend said plus what I heard since".
        applied,
        isLoading: feed.isLoading,
        error: feed.error,
        updatedAt: feed.dataUpdatedAt,
        reload: refetch,
        recount,
        stream,
        reconnect,
    };
}

// WHO IS WORKING RIGHT NOW.
//
// There is no "agents online" endpoint and there should not be: an agent is
// not a session that connects, it is a credential that acts. So the two facts
// that answer the question are the ones the system already writes — the
// token's `last_used_at`, and which token holds each reservation.
//
// ONLY ACTIVE TOKENS. A revoked one keeps its `last_used_at` forever and would
// sit in this list looking like somebody at work; `status` 2 is the filter, and
// it is also what stops a revoked token's leftover reservations from counting
// as a live agent.
//
// This list is not PATCHED from the channel — the notice (T15.5) carries no
// author, so it cannot say which token took a task. It is asked for again
// instead, whenever a notice moves a task into or out of `claimed`.
// Las tres clases de credencial, con el nombre que usa el formulario de tokens
// (features/tokens/utils/form.js). Mismo orden, para que la pantalla y el alta
// no se contradigan.
const KIND_SUMMARY = [
    { kind: 'agent_slave',  label: 'Esclavos' },
    { kind: 'agent_master', label: 'Maestros' },
    { kind: 'cli',          label: 'CLI' },
];

export function useAgents({ projectId } = {}) {
    const { can } = useAuth();
    const tokens = useTokens();

    const params = useMemo(() => {
        const query = { limit: 50, offset: 0, workflow_state: 'claimed', modality: ALL_MODALITIES };
        if (projectId) query.project_id = projectId;
        return query;
    }, [projectId]);

    const held = useQuery({
        queryKey: liveKeys.held(params),
        queryFn: ({ signal }) => api
            .get(livePaths.tasks(), { params, signal })
            .then(response => response.data?.items ?? []),
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const agents = useMemo(() => {
        const active = (tokens.data ?? []).filter(token => (
            String(token.kind).startsWith('agent_') && token.status === 2
        ));
        const tasks = held.data ?? [];
        // The clock this is judged against is the moment the data was fetched,
        // not the moment React happened to re-render. `Date.now()` here would
        // be an impure read that flips a reservation from live to expired on
        // an unrelated repaint, and it is also the wrong question: what can be
        // said honestly is what was true when we asked.
        const now = held.dataUpdatedAt || 0;

        return active
            .map(token => {
                const task = tasks.find(item => item.claimed_by_token === token.id) ?? null;
                const until = task?.claimed_until ? new Date(task.claimed_until).getTime() : null;
                // A reservation that ran out is not work in progress: the next
                // claim will take that task away from it without asking.
                const isExpired = task != null && until != null && until <= now;

                return {
                    id: token.id,
                    name: token.name,
                    kind: token.kind,
                    lastUsedAt: token.last_used_at ?? null,
                    task,
                    until: task?.claimed_until ?? null,
                    session: task?.claimed_session ?? null,
                    state: task ? (isExpired ? 'expired' : 'working') : 'idle',
                };
            })
            .sort((a, b) => {
                if ((a.state === 'working') !== (b.state === 'working')) {
                    return a.state === 'working' ? -1 : 1;
                }
                return new Date(b.lastUsedAt ?? 0) - new Date(a.lastUsedAt ?? 0);
            });
    }, [tokens.data, held.data, held.dataUpdatedAt]);

    // EL RECUENTO POR TIPO, que es lo que se mira cuando hay muchos. Las tres
    // clases del backend (`core/authz/roles.js`): esclavo, maestro y CLI.
    //
    // EL CLI ENTRA EN EL RECUENTO Y NO EN LA LISTA DE ARRIBA, porque es una
    // credencial de solo lectura: existe, se cuenta, y nunca va a tener una
    // tarea en la mano. Dejarlo fuera del todo hacía que la suma de la pantalla
    // no diera con la lista de tokens.
    const byKind = useMemo(() => {
        const active = (tokens.data ?? []).filter(token => token.status === 2);
        const held = new Map(agents.map(agent => [agent.id, agent.state]));

        return KIND_SUMMARY.map(({ kind, label }) => {
            const own = active.filter(token => token.kind === kind);
            return {
                kind,
                label,
                total: own.length,
                working: own.filter(token => held.get(token.id) === 'working').length,
                expired: own.filter(token => held.get(token.id) === 'expired').length,
                // El CLI no toma trabajo: la columna de «trabajando» no se
                // dibuja en cero, se dice que no aplica.
                takesWork: kind !== 'cli',
            };
        });
    }, [tokens.data, agents]);

    return {
        agents,
        byKind,
        working: agents.filter(agent => agent.state === 'working').length,
        // Without `tokens:admin` there is no list to join, and the block says
        // so instead of rendering an empty roster that reads as "nobody".
        isReadable: can('tokens:admin'),
        // The clock the ages on screen are measured from, so "hace 3 min" means
        // three minutes before we asked and not before the last repaint.
        countedAt: held.dataUpdatedAt,
        isLoading: tokens.isLoading || held.isLoading,
    };
}
